'use client'

import { useWallet } from '@/providers/WalletContext'
import { type Connection, PublicKey } from '@solana/web3.js'
import { useQuery } from '@tanstack/react-query'

// Token Program IDs
const TOKEN_PROGRAM_ID = new PublicKey('TokenkegQfeZyiNwAJbNbGKPFXCWuBvf9Ss623VQ5DA')
const TOKEN_2022_PROGRAM_ID = new PublicKey('TokenzQdBNbLqP5VEhdkAS6EPFLC1PHnBqCXEpPxuEb')

export type Position = {
	mint: string
	balance: number // in token units (not smallest)
	side: 'yes' | 'no'
	marketTicker?: string
	eventTicker?: string
	marketTitle?: string
	currentPrice?: number
}

type TokenAccountInfo = {
	pubkey: PublicKey
	account: {
		data: {
			parsed: {
				info: {
					mint: string
					tokenAmount: {
						uiAmount: number
						amount: string
						decimals: number
					}
				}
			}
		}
	}
}

// Fetch all token accounts for a user (both Token Program and Token-2022)
async function fetchTokenAccounts(
	connection: Connection,
	owner: string
): Promise<TokenAccountInfo[]> {
	const ownerPubkey = new PublicKey(owner)

	// Fetch from both token programs
	const [legacyAccounts, token2022Accounts] = await Promise.all([
		connection.getParsedTokenAccountsByOwner(ownerPubkey, {
			programId: TOKEN_PROGRAM_ID,
		}),
		connection.getParsedTokenAccountsByOwner(ownerPubkey, {
			programId: TOKEN_2022_PROGRAM_ID,
		}),
	])

	return [...legacyAccounts.value, ...token2022Accounts.value] as TokenAccountInfo[]
}

// Fetch positions for a specific market (by YES/NO mints)
export function useKalshiMarketPosition(yesMint?: string, noMint?: string) {
	const { solanaAddress, isSolanaReady, solanaConnection: connection } = useWallet()

	return useQuery({
		queryKey: ['kalshiPosition', solanaAddress, yesMint, noMint],
		queryFn: async (): Promise<{ yes: number; no: number }> => {
			if (!(connection && solanaAddress && yesMint && noMint)) {
				return { yes: 0, no: 0 }
			}

			const accounts = await fetchTokenAccounts(connection, solanaAddress)

			let yesBalance = 0
			let noBalance = 0

			for (const account of accounts) {
				const mint = account.account.data.parsed.info.mint
				const balance = account.account.data.parsed.info.tokenAmount.uiAmount || 0

				if (mint === yesMint) {
					yesBalance = balance
				} else if (mint === noMint) {
					noBalance = balance
				}
			}

			return { yes: yesBalance, no: noBalance }
		},
		enabled: isSolanaReady && !!solanaAddress && !!yesMint && !!noMint && !!connection,
		refetchInterval: 2000, // Refetch every 2 seconds
	})
}

// Fetch all Kalshi positions (all prediction market tokens)
export function useAllKalshiPositions() {
	const { solanaAddress, isSolanaReady, solanaConnection: connection } = useWallet()

	return useQuery({
		queryKey: ['allKalshiPositions', solanaAddress],
		queryFn: async (): Promise<Position[]> => {
			if (!(connection && solanaAddress)) {
				return []
			}

			// 1. Fetch all token accounts
			const accounts = await fetchTokenAccounts(connection, solanaAddress)

			// Filter out zero balances and known non-prediction tokens
			const USDC_MINT = 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v'
			const nonZeroTokens = accounts
				.map(acc => ({
					mint: acc.account.data.parsed.info.mint,
					balance: acc.account.data.parsed.info.tokenAmount.uiAmount || 0,
				}))
				.filter(t => t.balance > 0 && t.mint !== USDC_MINT)

			if (nonZeroTokens.length === 0) {
				return []
			}

			// 2. Filter to get only prediction market outcome mints (via server-side proxy)
			const allMints = nonZeroTokens.map(t => t.mint)
			const filterResponse = await fetch('/api/kalshi/filter-mints', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ addresses: allMints }),
			})

			if (!filterResponse.ok) {
				console.error('[useAllKalshiPositions] Failed to filter outcome mints')
				return []
			}

			const filterData = await filterResponse.json()
			const predictionMints: string[] = filterData.outcomeMints || []

			if (predictionMints.length === 0) {
				return []
			}

			// 3. Fetch market details for all outcome tokens (via server-side proxy)
			const marketsResponse = await fetch('/api/kalshi/markets-batch', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ mints: predictionMints }),
			})

			if (!marketsResponse.ok) {
				console.error('[useAllKalshiPositions] Failed to fetch markets batch')
				return []
			}

			const marketsData = await marketsResponse.json()
			const markets = marketsData.markets || []

			// Create a map by mint address
			const marketsByMint = new Map<string, { market: any; isYes: boolean }>()
			for (const market of markets) {
				if (market.accounts) {
					for (const account of Object.values(market.accounts) as any[]) {
						if (account.yesMint) {
							marketsByMint.set(account.yesMint, { market, isYes: true })
						}
						if (account.noMint) {
							marketsByMint.set(account.noMint, { market, isYes: false })
						}
					}
				}
			}

			// 4. Build positions
			const positions: Position[] = []
			for (const token of nonZeroTokens) {
				const marketInfo = marketsByMint.get(token.mint)
				if (marketInfo) {
					const { market, isYes } = marketInfo
					positions.push({
						mint: token.mint,
						balance: token.balance,
						side: isYes ? 'yes' : 'no',
						marketTicker: market.ticker.toUpperCase(),
						eventTicker: market.eventTicker.toUpperCase(),
						marketTitle: market.title,
						currentPrice: isYes
							? Number.parseFloat(market.yesBid || market.yesAsk || '0')
							: Number.parseFloat(market.noBid || market.noAsk || '0'),
					})
				}
			}

			return positions
		},
		enabled: isSolanaReady && !!solanaAddress && !!connection,
		refetchInterval: 2000,
	})
}
